import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Mail, Lock, User, Phone, ArrowRight, Building2, Clock, Check, X, ShieldCheck, Star, Zap } from 'lucide-react';
import api from '../../utils/api';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', phone: '', companyName: '',
    role: 'super-admin'
  });
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [trialConfig, setTrialConfig] = useState({ trialDurationDays: 14, maxUsers: 5 });
  const [showWelcome, setShowWelcome] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planIdFromUrl, setPlanIdFromUrl] = useState(null);

  const { login } = useAuth();
  const navigate  = useNavigate();
  const appName   = process.env.REACT_APP_APP_NAME || 'CRM Automax';

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    
    // Fetch trial config
    fetch(`${apiUrl}/subscriptions/config`)
      .then(r => r.json())
      .then(d => { 
        if (d) setTrialConfig({
          trialDurationDays: d.trialDurationDays || 14,
          maxUsers: d.maxUsers || 5
        }); 
      })
      .catch(() => {});

    // Check for planId in URL
    const params = new URLSearchParams(window.location.search);
    const pid = params.get('planId');
    if (pid) {
      setPlanIdFromUrl(pid);
      fetch(`${apiUrl}/subscription-plans/active`)
        .then(r => r.json())
        .then(plans => {
          const plan = plans.find(p => p._id === pid);
          if (plan) setSelectedPlan(plan);
        })
        .catch(() => {});
    }
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = { ...formData };
      if (planIdFromUrl) payload.planId = planIdFromUrl;

      const response = await api('/users/register', { 
        method: 'POST', 
        body: payload 
      });
      const data = await response.json();

      if (response.ok) {
        if (data.subscription?.trialEndDate) setTrialEndDate(new Date(data.subscription.trialEndDate));
        
        const loginResponse = await api('/users/login', {
          method: 'POST',
          body: { email: formData.email, password: formData.password }
        });
        if (loginResponse.ok) {
          const loginData = await loginResponse.json();
          login(loginData.user, loginData.token, loginData.subscription);
          setShowWelcome(true);
        } else {
          navigate('/login');
        }
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 12px 12px 42px',
    borderRadius: '10px', border: '1px solid #e2e8f0',
    fontSize: '14px', outline: 'none',
    background: '#ffffff', color: '#1e293b',
    boxSizing: 'border-box', transition: 'all 0.2s ease',
  };

  const labelStyle = { fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '8px', display: 'block' };

  return (
    <div className="register-layout" style={{ minHeight: '100vh', display: 'flex', background: '#ffffff', fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(15px);}to{opacity:1;transform:translateY(0);} }
        @keyframes scaleIn { from{opacity:0;transform:scale(0.95);}to{opacity:1;transform:scale(1);} }
        .reg-input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 4px rgba(59,130,246,0.1); }
      `}</style>

      {/* ── Left panel ── */}
      <div className="register-left-panel" style={{ flex: '0 0 440px', display: 'flex', flexDirection: 'column', background: '#f8fafc', borderRight: '1px solid #e2e8f0', padding: '60px 48px', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '64px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShieldCheck size={20} color="white" />
          </div>
          <span style={{ fontWeight: '800', fontSize: '18px', color: '#1e293b' }}>{appName}</span>
        </div>
        
        <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', lineHeight: 1.2, marginBottom: '40px' }}>
          Scale your sales team with confidence.
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {[
            { icon: <Zap size={18} color="#3b82f6" />, text: `${trialConfig.trialDurationDays}-day free trial, no credit card` },
            { icon: <Check size={18} color="#10b981" />, text: 'Unlimited lead management' },
            { icon: <Star size={18} color="#f59e0b" />, text: 'Advanced team collaboration' }
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#475569', fontSize: '15px', fontWeight: '500' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'white', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.icon}
              </div>
              {item.text}
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="register-right-panel" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 32px', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: '460px', animation: 'fadeUp 0.6s ease' }}>
          
          <div style={{ 
            background: selectedPlan ? '#f5f3ff' : '#f0f9ff', 
            border: selectedPlan ? '1px solid #ddd6fe' : '1px solid #bae6fd', 
            borderRadius: '12px', padding: '16px 20px', 
            display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '32px' 
          }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'white', border: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {selectedPlan ? <Zap size={20} color="#8b5cf6" /> : <Clock size={20} color="#0ea5e9" />}
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: selectedPlan ? '#6d28d9' : '#0369a1' }}>
                {selectedPlan ? `✓ ${selectedPlan.name} Plan` : `✓ ${trialConfig.trialDurationDays}-Day Free Trial`}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                {selectedPlan ? `₹${selectedPlan.price} / ${selectedPlan.durationDays} days · ${selectedPlan.maxUsers} Users` : `${trialConfig.maxUsers} Users · No CC required`}
              </div>
            </div>
          </div>

          <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '8px' }}>Create your workspace</h1>
          <p style={{ color: '#64748b', fontSize: '15px', marginBottom: '32px' }}>Join 500+ companies using {appName}.</p>

          {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '12px', borderRadius: '10px', marginBottom: '24px', fontSize: '13px', border: '1px solid #fee2e2' }}>{error}</div>}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div><label style={labelStyle}>Company Name</label><div style={{ position: 'relative' }}><Building2 size={17} style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} /><input className="reg-input" style={inputStyle} type="text" name="companyName" value={formData.companyName} onChange={handleChange} required placeholder="e.g. Acme Corp" /></div></div>
            <div><label style={labelStyle}>Full Name</label><div style={{ position: 'relative' }}><User size={17} style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} /><input className="reg-input" style={inputStyle} type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="John Doe" /></div></div>
            <div><label style={labelStyle}>Work Email</label><div style={{ position: 'relative' }}><Mail size={17} style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} /><input className="reg-input" style={inputStyle} type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="john@company.com" /></div></div>
            
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}><label style={labelStyle}>Password</label><input className="reg-input" style={{...inputStyle, paddingLeft: '13px'}} type="password" name="password" value={formData.password} onChange={handleChange} required placeholder="••••••••" /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Phone</label><input className="reg-input" style={{...inputStyle, paddingLeft: '13px'}} type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 ..." /></div>
            </div>

            {/* T&C Agreement */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '4px' }}>
              <input type="checkbox" id="agree-terms" required style={{ marginTop: '3px', accentColor: '#3b82f6', width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }} />
              <label htmlFor="agree-terms" style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.5, cursor: 'pointer' }}>
                I agree to the <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>Terms</a>, <a href="/privacy-policy" target="_blank" rel="noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>Privacy</a>, and <a href="/payment-policy" target="_blank" rel="noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>Refund Policy</a>.
              </label>
            </div>

            <button type="submit" disabled={loading} style={{ width: '100%', padding: '15px', background: loading ? '#93c5fd' : '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', marginTop: '8px', transition: 'all 0.2s ease' }}>
              {loading ? 'Setting up workspace...' : (selectedPlan ? `Start ${selectedPlan.name} Plan` : 'Start Free Trial')}
            </button>
          </form>

          <div style={{ marginTop: '32px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
            Already have an account? <Link to="/login" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '600' }}>Sign in</Link>
          </div>

          {/* Legal Footer Links */}
          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '16px' }}>
            {[
              { label: 'Privacy Policy', path: '/privacy-policy' },
              { label: 'Terms', path: '/terms' },
              { label: 'Payment Policy', path: '/payment-policy' },
              { label: 'Cookies', path: '/cookie-policy' },
            ].map(lnk => (
              <a key={lnk.path} href={lnk.path} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: '#94a3b8', textDecoration: 'none' }}>{lnk.label}</a>
            ))}
          </div>
        </div>
      </div>

      {/* ── Welcome Modal ── */}
      {showWelcome && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ width: '100%', maxWidth: '400px', background: '#ffffff', borderRadius: '24px', padding: '48px 40px', textAlign: 'center', animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <div style={{ width: '72px', height: '72px', borderRadius: '50%', margin: '0 auto 24px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={36} color="#22c55e" /></div>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', marginBottom: '10px' }}>Welcome aboard!</h2>
            <p style={{ color: '#64748b', fontSize: '15px', marginBottom: '28px' }}>
              {selectedPlan ? `Your ${selectedPlan.name} subscription is active.` : `Your ${trialConfig.trialDurationDays}-day free trial has started.`}
            </p>
            {trialEndDate && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '16px', marginBottom: '28px' }}>
                <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px' }}>Valid Until</div>
                <div style={{ fontSize: '20px', fontWeight: '800', color: '#3b82f6' }}>{trialEndDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              </div>
            )}
            <button onClick={() => navigate('/admin/dashboard')} style={{ width: '100%', padding: '14px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer' }}>Go to Dashboard</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;

